import React, { useContext } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  DateTimeController,
  Icons,
  PanelModal,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { getOffset, removeSecondsAndTimezone } from "../../../utils/dateUtils";
import { postUpdateExpireAppointment } from "../../../apis/appointmentApi";

interface Form {
  date: string;
}

interface AppointmentChangeExpirationModalProps {
  userUuid?: string;
  appointmentUuid?: string;
  expiresAt?: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: () => void;
}

function AppointmentChangeExpirationModal(
  props: AppointmentChangeExpirationModalProps,
) {
  const { visible, onClose } = props;

  return (
    <PanelModal
      width="400px"
      visible={visible}
      closeModal={onClose}
      disableClickAway
      disableEscapeKeyDown
      verticalAlign="top"
    >
      <AppointmentChangeExpirationModalContent {...props} />
    </PanelModal>
  );
}

function AppointmentChangeExpirationModalContent(
  props: AppointmentChangeExpirationModalProps,
) {
  const { userUuid, appointmentUuid, expiresAt, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const postUpdateExpireMutation = useMutation(postUpdateExpireAppointment);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<Form>({
    mode: "onTouched",
    defaultValues: {
      date: removeSecondsAndTimezone(expiresAt) || "",
    },
  });

  const { date } = useWatch({
    control,
  });

  const save = async (values: Form) => {
    if (!userUuid) {
      throw new Error("userUuid is empty");
    }
    if (!appointmentUuid) {
      throw new Error("appointmentUuid is empty");
    }
    if (!expiresAt) {
      throw new Error("Invalid expiresAt time");
    }
    try {
      await postUpdateExpireMutation.mutateAsync({
        clientUuid: app.client!.uuid,
        userUuid,
        appointmentUuid,
        payload: {
          date: `${values.date}:00.000Z`,
        },
      });
      toast.success("Expiration changed", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const handleOk = handleSubmit(save);

  const okLoading = postUpdateExpireMutation.isLoading;
  const okDisabled = !isValid || removeSecondsAndTimezone(expiresAt) === date;

  return (
    <>
      <PanelModal.Header title="Change expiration">
        <Button
          text="Close"
          buttonType="secondary"
          size="medium"
          onClick={onClose}
        />
        <Button
          text="OK"
          buttonType="primary"
          size="medium"
          disabled={okDisabled}
          loading={okLoading}
          onClick={handleOk}
        />
      </PanelModal.Header>
      <PanelModal.Body paddingSize="none">
        <div className="pt-4 pr-4 pb-0 pl-4">
          <form onSubmit={handleOk}>
            <DateTimeController
              control={control}
              name="date"
              label="When"
              required
              DateInputProps={{ startIcon: Icons.Classes }}
              TimeInputProps={{ startIcon: Icons.Clock, minuteStep: 5 }}
            />
          </form>
        </div>
      </PanelModal.Body>
    </>
  );
}

export default AppointmentChangeExpirationModal;
